import React, { useState, useEffect, useCallback, useRef } from 'react';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import { useAuth } from '../../contexts/auth';
import notify from 'devextreme/ui/notify';
import { Button } from 'devextreme-react/button';
import { SelectBox } from 'devextreme-react/select-box';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {getAPIData, getUserDetails, adminGetCompanies, updateAPIReportData, getAPIAIHelp} from '../../api/api'
import {iogpValues, yesnoValues, causalValues, sifStatusValues} from '../../api/api'
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { groupBy } from '../../utils/miscUtil';
import {
  SIFEventDetails,
  SIFEventClass,
} from '../../components';

import './sif.scss';

var categories = require('../../api/CategoryTree.json');
categories = groupBy(categories, 'Category')
var categoriesList = Object.keys(categories);

var disciplines = require('../../api/DiscipineTree.json');
disciplines = groupBy(disciplines, 'Discipline')
var disciplinesList = Object.keys(disciplines);

var iogpList = iogpValues.map((it) => it.value)
var yesnoList = yesnoValues.map((it) => it.value);

var causalList = causalValues.map((it) => it.value);

export default function SIFEvent() {
  const navigate = useNavigate();
  let [searchParams, setSearchParams] = useSearchParams();
  const eventID = searchParams.get("event_id");
  const companyID = searchParams.get("company_id");
  const { user, refreshSession } = useAuth();
  const tabsRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isWriter, setIsWriter] = useState(false);

  const [sifEvent, setSifEvent] = useState({});
  const [companyDetails, setCompanyDetails] = useState(null);
  const [companiesList, setCompaniesList] = useState(null);
  const [existingData, setExistingData] = useState(null);
  const [companyExistingData, setCompanyExistingData] = useState(null);
  const [duplicateEvent, setDuplicateEvent] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const step1ValidRef = useRef(false);
  const [step1Valid, setStep1Valid] = useState(false);
  const [step2Valid, setStep2Valid] = useState(false);
  const [aiHelpQuery, setAiHelpQuery] = useState({});

  const onDataChanged = useCallback((data) => {
    setSifEvent(data);
    var found = false;
    if (companyExistingData) {
      for (var i=0; i<companyExistingData.length; i++) {
        if ((new Date(companyExistingData[i]['DATE OF EVENT (m/d/yyyy)']))?.getTime()===data['DATE OF EVENT (m/d/yyyy)']?.getTime()) {
          if (data['DESCRIPTION OF INCIDENT']===companyExistingData[i]['DESCRIPTION OF INCIDENT']) {
            found = true;
            break;
          }
        }
      }
    }

    if (eventID === null) {
      setDuplicateEvent(found);
    }

  }, [companyExistingData, eventID])

  const onStep1Valid = useCallback((data) => {
    step1ValidRef.current = data;
    setStep1Valid(data);
  }, [setStep1Valid])

  const onStep2Valid = useCallback((data) => {
    setStep2Valid(data);
  }, [setStep2Valid])

  const onError = useCallback((error) => {
    if (error && error.status === 401) {
      refreshSession();
    } else {
      notify("An error has occured. Please try again later.", 'error', 2000);
      console.log(error);
      let aiHQ =
        {
          'SIF CATEGORY': false,
          'SIF Condition': false,
          'IOGP Life-Saving Rule Violation': false,
          'Discipline of Activity at Time of Event': false,
          'Activity at Time of Event': false,
          'Activity Subgroup or Task': false,
          'Was it reasonable that a person was or could have been in proximity at the time of the event?': false,
          'Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?': false,
          'Primary Causal Factors': false,
        };
      setAiHelpQuery({...aiHQ});
    }
  }, [refreshSession]);

  const onAIError = useCallback((error) => {
    notify("We cannot auto-categorize the event. Please try again later.", 'warning', 2000);
    console.log(error);
    let aiHQ =
      {
        'SIF CATEGORY': false,
        'SIF Condition': false,
        'IOGP Life-Saving Rule Violation': false,
        'Discipline of Activity at Time of Event': false,
        'Activity at Time of Event': false,
        'Activity Subgroup or Task': false,
        'Was it reasonable that a person was or could have been in proximity at the time of the event?': false,
        'Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?': false,
        'Primary Causal Factors': false,
      };
    setAiHelpQuery({...aiHQ});
  }, []);

  const onAIConditionSuccess = useCallback((data) => {
    if (!sifEvent['aiResponse']) {
      sifEvent['aiResponse'] = {};
    }
    sifEvent['aiResponse']['SIF Condition'] = data;
    aiHelpQuery['SIF Condition'] = false;
    setAiHelpQuery({...aiHelpQuery});
    let cl = categories[sifEvent['SIF CATEGORY']];
    if (cl) {
      cl = groupBy(cl, "Condition");
      let conditionList = Object.keys(cl);
      if (conditionList.includes(data)) {
        sifEvent['SIF Condition'] = data;
      }
    }
    setSifEvent({...sifEvent});
  }, [sifEvent, aiHelpQuery]);

  const onAICategorySuccess = useCallback((data) => {
    if (!sifEvent['aiResponse']) {
      sifEvent['aiResponse'] = {};
    }
    sifEvent['aiResponse']['SIF CATEGORY'] = data;
    aiHelpQuery['SIF CATEGORY'] = false;
    if (categoriesList.includes(data)) {
      sifEvent['SIF CATEGORY'] = data;
      sifEvent['SIF Condition'] = null;
      let cl = categories[data];
      if (cl) {
        cl = groupBy(cl, "Condition");
        let conditionList = Object.keys(cl);
        aiHelpQuery['SIF Condition'] = true;
        getAPIAIHelp(
          "/reports/aihelp/sif",
          user,
          {query:'Given that the category is '+data+', what is the condition for the safety incident between """ . Choose one of the following: '+conditionList.join("\n ")+'\nRespond with only the condition name.\n"""'+sifEvent['DESCRIPTION OF INCIDENT']+'""""'},
          onAIConditionSuccess,
          onAIError,
        );
      }
    }
    setSifEvent({...sifEvent});
    setAiHelpQuery({...aiHelpQuery});
  }, [sifEvent, aiHelpQuery, user, onAIConditionSuccess, onAIError]);

  const onAISubactivitySuccess = useCallback((data) => {
    if (!sifEvent['aiResponse']) {
      sifEvent['aiResponse'] = {};
    }
    sifEvent['aiResponse']['Activity Subgroup or Task'] = data;
    aiHelpQuery['Activity Subgroup or Task'] = false;
    setAiHelpQuery({...aiHelpQuery});
    let lst = disciplines[sifEvent['Discipline of Activity at Time of Event']];
    if (lst) {
      lst = groupBy(lst, "Activity");
      lst = lst[sifEvent['Activity at Time of Event']];
      if (lst) {
        lst = groupBy(lst, "Subactivity");
        let subList = Object.keys(lst);
        if (subList.includes(data)) {
          sifEvent['Activity Subgroup or Task'] = data;
        }
      }
    }
    setSifEvent({...sifEvent});
  }, [sifEvent, aiHelpQuery]);

  const onAIActivitySuccess = useCallback((data) => {
    if (!sifEvent['aiResponse']) {
      sifEvent['aiResponse'] = {};
    }
    sifEvent['aiResponse']['Activity at Time of Event'] = data;
    aiHelpQuery['Activity at Time of Event'] = false;
    let activities = disciplines[sifEvent['Discipline of Activity at Time of Event']];
    if (activities) {
      activities = groupBy(activities, "Activity");
      let al = Object.keys(activities);
      if (al.includes(data)) {
        sifEvent['Activity at Time of Event'] = data;
        sifEvent['Activity Subgroup or Task'] = null;
        aiHelpQuery['Activity Subgroup or Task'] = true;
        let tl = activities[data];
        if (tl) {
          tl = groupBy(tl, "Subactivity");
          let subList = Object.keys(tl);
          getAPIAIHelp(
            "/reports/aihelp/sif",
            user,
            {query:'Given that the discipline of activity is '+sifEvent['Discipline of Activity at Time of Event']+' and the activity is '+data+', what is the ongoing sub-activity for the safety incident between """? Choose one of the following: '+subList.join("\n ")+'\nRespond with only the sub-activity name.\n"""'+sifEvent['DESCRIPTION OF INCIDENT']+'""""'},
            onAISubactivitySuccess,
            onAIError,
          );
        }
      }
    }
    setSifEvent({...sifEvent});
    setAiHelpQuery({...aiHelpQuery});
  }, [sifEvent, aiHelpQuery, user, onAISubactivitySuccess, onAIError]);

  const onAIDisciplineSuccess = useCallback((data) => {
    if (!sifEvent['aiResponse']) {
      sifEvent['aiResponse'] = {};
    }
    sifEvent['aiResponse']['Discipline of Activity at Time of Event'] = data;
    aiHelpQuery['Discipline of Activity at Time of Event'] = false;
    if (disciplinesList.includes(data)) {
      sifEvent['Discipline of Activity at Time of Event'] = data;
      sifEvent['Activity at Time of Event'] = null;
      sifEvent['Activity Subgroup or Task'] = null;
      let dl = disciplines[data];
      if (dl) {
        dl = groupBy(dl, "Activity");
        let dlKeys = Object.keys(dl);
        aiHelpQuery['Activity at Time of Event'] = true;
        getAPIAIHelp(
          "/reports/aihelp/sif",
          user,
          {query:'Given that the discipline of activity is '+data+', what is the ongoing activity for the safety incident between """? Choose one of the following: '+dlKeys.join("\n ")+'\nRespond with only the activity name.\n"""'+sifEvent['DESCRIPTION OF INCIDENT']+'""""'},
          onAIActivitySuccess,
          onAIError,
        );
      }
    }
    setSifEvent({...sifEvent});
    setAiHelpQuery({...aiHelpQuery});
  }, [sifEvent, aiHelpQuery, user, onAIActivitySuccess, onAIError]);

  const onAIIOGPSuccess = useCallback((data) => {
    if (!sifEvent['aiResponse']) {
      sifEvent['aiResponse'] = {};
    }
    sifEvent['aiResponse']['IOGP Life-Saving Rule Violation'] = data;
    aiHelpQuery['IOGP Life-Saving Rule Violation'] = false;
    setAiHelpQuery({...aiHelpQuery});
    if (iogpList.includes(data)) {
      sifEvent['IOGP Life-Saving Rule Violation'] = data;
    }
    setSifEvent({...sifEvent});
  }, [sifEvent, aiHelpQuery]);

  const onAIOtherQ1Success = useCallback((data) => {
    if (!sifEvent['aiResponse']) {
      sifEvent['aiResponse'] = {};
    }
    sifEvent['aiResponse']['Was it reasonable that a person was or could have been in proximity at the time of the event?'] = data;
    aiHelpQuery['Was it reasonable that a person was or could have been in proximity at the time of the event?'] = false;
    setAiHelpQuery({...aiHelpQuery});
    if (yesnoList.includes(data)) {
      sifEvent['Was it reasonable that a person was or could have been in proximity at the time of the event?'] = data;
    }
    setSifEvent({...sifEvent});
  }, [sifEvent, aiHelpQuery]);

  const onAIOtherQ2Success = useCallback((data) => {
    if (!sifEvent['aiResponse']) {
      sifEvent['aiResponse'] = {};
    }
    sifEvent['aiResponse']['Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?'] = data;
    aiHelpQuery['Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?'] = false;
    setAiHelpQuery({...aiHelpQuery});
    if (yesnoList.includes(data)) {
      sifEvent['Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?'] = data;
    }
    setSifEvent({...sifEvent});
  }, [sifEvent, aiHelpQuery]);

  const onAIOtherQ3Success = useCallback((data) => {
    if (!sifEvent['aiResponse']) {
      sifEvent['aiResponse'] = {};
    }
    sifEvent['aiResponse']['Primary Causal Factors'] = data;
    aiHelpQuery['Primary Causal Factors'] = false;
    setAiHelpQuery({...aiHelpQuery});
    if (causalList.includes(data)) {
      sifEvent['Primary Causal Factors'] = data;
    }
    setSifEvent({...sifEvent});
  }, [sifEvent, aiHelpQuery]);

  const onStep1Next = useCallback(() => {
    step1ValidRef.current = true;
    setStep1Valid(true);
    setSelectedTabIndex(1);
    if (tabsRef.current) {
      tabsRef.current.instance().option("selectedIndex", 1);
    }
    let aiHQ =
      {
        'SIF CATEGORY': false,
        'SIF Condition': false,
        'IOGP Life-Saving Rule Violation': false,
        'Discipline of Activity at Time of Event': false,
        'Activity at Time of Event': false,
        'Activity Subgroup or Task': false,
        'Was it reasonable that a person was or could have been in proximity at the time of the event?': false,
        'Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?': false,
        'Primary Causal Factors': false,
      };

    setAiHelpQuery({...aiHQ});
    if (sifEvent['DESCRIPTION OF INCIDENT'] && sifEvent['DESCRIPTION OF INCIDENT'].split(" ").length > 9) {
      if (!sifEvent['SIF CATEGORY']) {
        aiHQ['SIF CATEGORY'] = true;
        getAPIAIHelp(
          "/reports/aihelp/sif",
          user,
          {query:'Categorize the safety incident between """ in one of the following categories: '+categoriesList.join("\n ")+'\nRespond with only the category name.\n"""'+sifEvent['DESCRIPTION OF INCIDENT']+'""""'},
          onAICategorySuccess,
          onAIError,
        );
      }

      if (!sifEvent['IOGP Life-Saving Rule Violation']) {
        aiHQ['IOGP Life-Saving Rule Violation'] = true;
        getAPIAIHelp(
          "/reports/aihelp/sif",
          user,
          {query:'Which of the following IOGP Life-Saving Rule violation was the main reason for the safety incident between """. Choose one of the following: '+iogpList.join("\n ")+'\nRespond with only the violation name.\n"""'+sifEvent['DESCRIPTION OF INCIDENT']+'""""'},
          onAIIOGPSuccess,
          onAIError,
        );
      }
      if (!sifEvent['Discipline of Activity at Time of Event']) {
        aiHQ['Discipline of Activity at Time of Event'] = true;
        getAPIAIHelp(
          "/reports/aihelp/sif",
          user,
          {query:'What is the ongoing discipline of activity for the safety incident between """. Choose one of the following: '+disciplinesList.join("\n ")+'\nRespond with only the discipline name. \n"""'+sifEvent['DESCRIPTION OF INCIDENT']+'""""'},
          onAIDisciplineSuccess,
          onAIError,
        );
      }
    }

    setAiHelpQuery({...aiHQ});
  }, [user, sifEvent, onAICategorySuccess, onAIDisciplineSuccess, onAIIOGPSuccess, onAIError]);

  const onUploadSuccess = useCallback((data) => {
    notify("Data uploaded succesfully.", 'success', 5000);
    setLoading(false);
    navigate("/sifdata");
  }, [navigate]);

  const onSave = useCallback(() => {
    setLoading(true);
    const eventToSave = { ...sifEvent };
    if (!eventToSave['company_id']) {
      eventToSave['company_id'] = companyDetails['company_id'];
    }
    delete eventToSave['Type'];
    updateAPIReportData("/reports/data/sif/event", user, eventToSave, onUploadSuccess, onError);
  }, [user, sifEvent, companyDetails, onUploadSuccess, onError]);

  const onTabChanging = useCallback((e) => {
    if ((e.addedItems?.length) && (e.addedItems[0]?.title === "Event Classification")) {
      if (!step1ValidRef.current) {
        e.cancel = true;
      }
    }
  }, []);

  const onDataSuccess = useCallback((data) => {
    if (data['DATE OF EVENT (m/d/yyyy)']) {
      data['DATE OF EVENT (m/d/yyyy)'] = new Date(data['DATE OF EVENT (m/d/yyyy)']);
    }
    if (!data['Type']) {
      if (data['Potential or Actual SIF']) {
        data['Type'] = data['Potential or Actual SIF'];
      } else if ((parseInt(data['Number of Fatalities']) || 0) + (parseInt(data['Number of Serious Injuries']) || 0) > 0) {
        data['Type'] = "Actual";
      } else {
        data['Type'] = "Potential";
      }
    }
    if (data['Number of Fatalities'] === undefined || data['Number of Fatalities'] === null) {
      data['Number of Fatalities'] = 0;
    }
    if (data['Number of Serious Injuries'] === undefined || data['Number of Serious Injuries'] === null) {
      data['Number of Serious Injuries'] = 0;
    }
    setSifEvent(prev => ({
      ...prev,
      ...data,
      ...(data['Company'] ? { Company: data['Company'] } : (prev?.Company ? { Company: prev.Company } : {}))
    }));
    setLoading(false);
  }, [setSifEvent, setLoading]);

  const onUserDetailsSuccess = useCallback((data) => {
    setCompanyDetails(data);
    if (data?.company_name) {
      setSifEvent(prev => ({
        ...prev,
        Company: data.company_name,
        company_id: data.company_id,
      }));
    }
  }, [setCompanyDetails, setSifEvent]);

  const onGetCompaniesSuccess = useCallback((data) => {
    data = data.sort((a,b) => (a.company_name > b.company_name) ? 1 : ((b.company_name > a.company_name) ? -1 : 0));
    setCompaniesList(data);
  }, [setCompaniesList]);

  const onCompanySelected = useCallback((e) => {
    if (!companiesList) {
      return;
    }
    const val = e.value || e;
    for (var i=0; i<companiesList.length; i++){
      if (companiesList[i].company_id === val) {
        const comp = companiesList[i];
        setCompanyDetails(comp);
        setSifEvent(prev => ({
          ...prev,
          Company: comp.company_name,
          company_id: comp.company_id,
        }));
        break;
      }
    }
  }, [companiesList, setCompanyDetails, setSifEvent]);

  const onGetAllDataSuccess = useCallback((data) => {
    setExistingData(data);
  }, [setExistingData]);

  useEffect(() => {
    const targetId = companyID || sifEvent?.company_id;
    if (!targetId || !companiesList) {
      return;
    }
    const found = companiesList.find(c => c.company_id === targetId);
    if (found) {
      setCompanyDetails(prev => (prev?.company_id === found.company_id ? prev : found));
      setSifEvent(prev => {
        if (prev && prev.Company === found.company_name && prev.company_id === found.company_id) {
          return prev;
        }
        return {
          ...prev,
          Company: found.company_name,
          company_id: found.company_id,
        };
      });
    }
  }, [companyID, sifEvent?.company_id, companiesList]);

  useEffect(() => {
    let keys = Object.keys(aiHelpQuery);
    if (keys.length > 0) {
      var working = false;
      for (var i=0; i<keys.length; i++) {
        if (aiHelpQuery[keys[i]]) {
          working = true;
          break;
        }
      }
      if (!working) {
        //tabsRef.current.instance().option("selectedIndex",1);
      }
    }

  }, [aiHelpQuery]);

  useEffect(() => {
    if (!companyDetails) {
      return;
    }
    setSifEvent(prev => {
      if (prev && prev.Company === companyDetails["company_name"] && prev.company_id === companyDetails["company_id"]) {
        return prev;
      }
      return {
        ...prev,
        Company: companyDetails["company_name"],
        company_id: companyDetails["company_id"],
      };
    });
  }, [companyDetails]);

  useEffect(() => {
    if (!companyDetails || !existingData) {
      return;
    }
    var currentData = groupBy(existingData, 'company_id');
    setCompanyExistingData(currentData[companyDetails['company_id']] || []);
  }, [companyDetails, existingData]);

  useEffect(() => {
    if (user.signInUserSession) {
      let groups = user.signInUserSession.idToken.payload['cognito:groups']
      var has_perm=false;
      if (groups) {
        for (var i=0; i<groups.length; i++) {
          if (groups[i]==='admin') {
            has_perm=true;
            setIsAdmin(true);
            setIsWriter(true);
            adminGetCompanies(user, onGetCompaniesSuccess, onError);
            break;
          }
          if (groups[i].includes("sifrw")) {
            has_perm=true;
            setIsWriter(true);
            getUserDetails(user, onUserDetailsSuccess, onError);
            break;
          }
        }
      }

      if (!has_perm) {
        notify("Insufficient permissions. Please contact the system administrator.", 'error', 5000);
        navigate('/home');
        return
      }
      if (eventID) {
        getAPIData('/reports/data/sif/event/'+companyID+"/"+eventID, user, onDataSuccess, onError);
      } else {
        setSifEvent(prev => ({
          "Type": "Potential",
          "Number of Fatalities": 0,
          "Number of Serious Injuries": 0,
          ...prev,
        }));
        setLoading(false);
      }
      getAPIData("/reports/data/sif", user, onGetAllDataSuccess, onError);
    }
  }, [user, companyID, eventID, navigate, onDataSuccess, onGetAllDataSuccess, onError, onUserDetailsSuccess, onGetCompaniesSuccess]);

  if(!loading) {
  return (
    <React.Fragment>
      <div className='content-block d-flex flex-row justify-content-between'>
      {eventID?<h2>Edit Event</h2>:<h2>Add Event</h2>}
      <div className='d-flex flex-row'>
      {isWriter && false && <Button
        text="Company Data"
        stylingMode="outlined"
        type="default"
        onClick={() => {navigate("/sifdata")}}
      />}
      {isAdmin && isWriter && <div className="px-2"/>}
      {isAdmin && false && <Button
        text="Upload SIF Data"
        stylingMode="outlined"
        type="danger"
        onClick={() => {navigate("/sifupload")}}
      />}
      </div>
      </div>
      <hr className="mt-0 mb-5"></hr>

      <div className={'content-block'}>
        {isAdmin && !eventID && companiesList && <div className="mb-4">
          <h5>Select the company you are reporting data for</h5>
          <SelectBox
              dataSource={companiesList}
              showDropDownButton={true}
              activeStateEnabled={true}
              searchEnabled={true}
              valueExpr='company_id'
              displayExpr='company_name'
              value={companyDetails?.company_id || null}
              onValueChanged={onCompanySelected}
          >
          </SelectBox>
        </div>}
        <div style={{height:"100px"}}>
          {duplicateEvent &&
            <div className="alert alert-warning" role="alert" >
              <i className="fas fa-exclamation-triangle"></i> Warning: There is already an event on this date. Please verify that this is not a duplicate.
            </div>
          }
          {sifEvent['DESCRIPTION OF INCIDENT'] && sifEvent['DESCRIPTION OF INCIDENT'].length > 20 && sifEvent['DESCRIPTION OF INCIDENT'].split(" ").length < 10 &&
            <div className="alert alert-warning" role="alert" >
              <i className="fas fa-exclamation-triangle"></i> Warning: The provided event description is too short and we won't be able to auto-categorize it. Please make sure it's at least 10 words.
            </div>
          }
          {sifEvent && sifEvent["Type"]==="Actual" && sifEvent["Number of Fatalities"]===0 && sifEvent["Number of Serious Injuries"]===0 &&
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle"></i> Error: An actual event has to have at least one fatality or serious injury.
            </div>
          }
        </div>

        {sifEvent && companyDetails && <div className={'dx-card responsive-paddings'}>
          <TabPanel
            ref={tabsRef}
            selectedIndex={selectedTabIndex}
            onSelectedIndexChange={setSelectedTabIndex}
            width="100%"
            height={"auto"}
            swipeEnabled={true}
            tabsPosition={'left'}
            stylingMode={'secondary'}
            iconPosition={'left'}
            onSelectionChanging={onTabChanging}
            animationEnabled={false}
          >
            <TabItem title="Event Details" icon="far fa-clipboard">
              <div className="tabpanel-item px-3">
                <SIFEventDetails
                  data={sifEvent}
                  onDataChanged={onDataChanged}
                  onCancel={() => {navigate("/sifdata")}}
                  onCTA={onStep1Next}
                  onValidData={onStep1Valid}
                />
              </div>
            </TabItem>
            <TabItem title="Event Classification" icon="fas fa-layer-group">
              <div className="tabpanel-item px-3">
                <SIFEventClass
                  data={sifEvent}
                  onDataChanged={onDataChanged}
                  onCancel={() => {navigate("/sifdata")}}
                  onCTA={onSave}
                  onValidData={onStep2Valid}
                  aiLoading={aiHelpQuery}
                />
              </div>
            </TabItem>
          </TabPanel>
        </div>}
      </div>
    </React.Fragment>
  )
} else {
  return (
    <div className="d-flex flex-row justify-content-center align-items-center mt-5 pt-5">
        <LoadIndicator
            id="large-indicator"
            height={60}
            width={60}
        />
    </div>
  )
}

}
